import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { RoleType } from "utils/constants"
import { Chat, Message } from "modules/Chat/utils/interfaces"
import { sortChatsByLastMessage } from "modules/Chat/utils/sortChatsByLastMessage"

interface ChatState {
    activeChatId: number | null
    chats: Chat[]
    isLoading: boolean
    messages: Message[]
    role: RoleType | undefined
    token: string | null
}

const initialState: ChatState = {
    activeChatId: null,
    chats: [],
    isLoading: true,
    messages: [],
    role: undefined,
    token: null,
}

const chatSlice = createSlice({
    name: "chat",
    initialState,
    reducers: {
        addMessage(state, action: PayloadAction<Message>) {
            const existingMessage = state.messages.find((msg) => msg.hash === action.payload.hash)
            if (!existingMessage) {
                state.messages.push(action.payload)
            }
        },
        addMessages(state, action: PayloadAction<Message[]>) {
            const newMessages = action.payload.filter((newMessage) => {
                return !state.messages.some(
                    (existingMessage) =>
                        existingMessage.hash === newMessage.hash &&
                        existingMessage.status === newMessage.status
                )
            })

            if (newMessages.length > 0) {
                state.messages = [...state.messages, ...newMessages]
            }
        },
        setActiveChatId(state, action: PayloadAction<number>) {
            state.activeChatId = action.payload
        },
        setChats(state, action: PayloadAction<Chat[]>) {
            state.chats = sortChatsByLastMessage(action.payload)
        },
        setIsLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload
        },
        setMessages(state, action: PayloadAction<Message[]>) {
            state.messages = action.payload
        },
        setRole(state, action: PayloadAction<RoleType>) {
            state.role = action.payload
        },
        setToken(state, action: PayloadAction<string>) {
            state.token = action.payload
        },
        updateMessage(state, action: PayloadAction<Partial<Message>>) {
            const key = action.payload.hash ? "hash" : "id"

            const message = state.messages.find((message) => message[key] === action.payload[key])

            if (!message) return

            Object.assign(message, action.payload)
        },
        updateChat(state, action: PayloadAction<Partial<Chat>>) {
            const chat = state.chats.find((chat) => chat.chatId === action.payload.chatId)

            if (!chat) return

            Object.assign(chat, action.payload)
            state.chats = sortChatsByLastMessage(state.chats)
        },
    },
})

export const chatReducer = chatSlice.reducer

export const {
    addMessage,
    addMessages,
    setActiveChatId,
    setChats,
    setIsLoading,
    setMessages,
    setRole,
    setToken,
    updateChat,
    updateMessage,
} = chatSlice.actions
